import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReportTemplates = ({ onSelectTemplate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templates = [
    {
      id: 'monthly-summary',
      name: 'Monthly Compliance Summary',
      description: 'Comprehensive overview of monthly compliance activities and violations',
      icon: 'Calendar',
      frequency: 'Monthly',
      lastGenerated: '2024-09-01',
      parameters: ['All Teams', 'All Platforms', 'All Violation Types'],
      color: 'bg-accent/10 text-accent'
    },
    {
      id: 'audit-preparation',
      name: 'Audit Preparation Report',
      description: 'Detailed documentation for regulatory audit requirements',
      icon: 'Shield',
      frequency: 'Quarterly',
      lastGenerated: '2024-07-01',
      parameters: ['Critical & High Violations', 'Resolution Timeline', 'Corrective Actions'],
      color: 'bg-warning/10 text-warning'
    },
    {
      id: 'regulatory-filing',
      name: 'Regulatory Filing Documentation',
      description: 'Formatted reports for regulatory body submissions',
      icon: 'FileText',
      frequency: 'As Needed',
      lastGenerated: '2024-08-15',
      parameters: ['Specific Date Range', 'Violation Categories', 'Legal Citations'],
      color: 'bg-success/10 text-success'
    },
    {
      id: 'team-performance',
      name: 'Team Performance Analysis',
      description: 'Individual team compliance metrics and improvement areas',
      icon: 'Users',
      frequency: 'Weekly',
      lastGenerated: '2024-09-16',
      parameters: ['Team Comparison', 'Resolution Times', 'Training Needs'],
      color: 'bg-primary/10 text-primary'
    },
    {
      id: 'platform-analysis',
      name: 'Platform Risk Analysis',
      description: 'Platform-specific violation patterns and risk assessment',
      icon: 'BarChart3',
      frequency: 'Bi-weekly',
      lastGenerated: '2024-09-10',
      parameters: ['Platform Metrics', 'Content Types', 'Risk Scores'],
      color: 'bg-secondary/10 text-secondary'
    },
    {
      id: 'executive-dashboard',
      name: 'Executive Dashboard',
      description: 'High-level compliance overview for leadership reporting',
      icon: 'TrendingUp',
      frequency: 'Monthly',
      lastGenerated: '2024-09-01',
      parameters: ['Key Metrics', 'Trend Analysis', 'Strategic Insights'],
      color: 'bg-error/10 text-error'
    }
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template?.id);
    onSelectTemplate(template);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Report Templates</h3>
          <p className="text-sm text-muted-foreground">Pre-configured report templates for common compliance needs</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
        >
          Create Template
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates?.map((template) => (
          <div
            key={template?.id}
            className={`border border-border rounded-lg p-4 cursor-pointer transition-micro hover-scale hover:border-accent/50 ${
              selectedTemplate === template?.id ? 'border-accent bg-accent/5' : ''
            }`}
            onClick={() => handleTemplateSelect(template)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2 rounded-lg ${template?.color}`}>
                <Icon name={template?.icon} size={20} />
              </div>
              <div className="text-xs text-muted-foreground">
                {template?.frequency}
              </div>
            </div>

            <h4 className="font-medium text-foreground mb-2">{template?.name}</h4>
            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
              {template?.description}
            </p>

            <div className="space-y-2 mb-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Includes:
              </div>
              <div className="flex flex-wrap gap-1">
                {template?.parameters?.slice(0, 2)?.map((param, index) => (
                  <span
                    key={index}
                    className="inline-flex px-2 py-1 text-xs bg-muted text-muted-foreground rounded-md"
                  >
                    {param}
                  </span>
                ))}
                {template?.parameters?.length > 2 && (
                  <span className="inline-flex px-2 py-1 text-xs bg-muted text-muted-foreground rounded-md">
                    +{template?.parameters?.length - 2} more
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Last generated: {formatDate(template?.lastGenerated)}</span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="Settings"
                  className="h-6 w-6 p-0"
                />
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="Play"
                  className="h-6 w-6 p-0"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {selectedTemplate && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Selected: {templates?.find(t => t?.id === selectedTemplate)?.name}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                iconPosition="left"
              >
                Customize
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Play"
                iconPosition="left"
              >
                Generate Report
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportTemplates;